// src/services/api.js
import axios from 'axios';
import { clearLocalSession, redirectToUnifiedAuth } from '../utils/unifiedAuth';

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const isDemo = window.location.hostname.includes('demo');

const withTrailingSlash = (value) => {
  if (!value) return '';
  return value.endsWith('/') ? value : `${value}/`;
};

let baseURL;
if (isLocalhost) {
  baseURL = 'http://localhost:8000/api/';
  console.log('🔧 API Environment: LOCALHOST');
} else if (isDemo) {
  const currentProtocol = window.location.protocol;
  const currentHost = window.location.hostname;
  baseURL = `${currentProtocol}//${currentHost}/api/`;
  console.log('🎯 API Environment: DEMO');
  console.log('   Hostname:', currentHost);
} else {
  const currentProtocol = window.location.protocol;
  const currentHost = window.location.hostname;
  baseURL = `${currentProtocol}//${currentHost}/api/`;
  console.log('🚀 API Environment: PRODUCTION');
  console.log('   Hostname:', currentHost);
}

const authBaseURL = withTrailingSlash((process.env.REACT_APP_AUTH_API_URL || '').trim()) || baseURL;

console.log('📡 API Base URL:', baseURL);
console.log('🔐 Auth API Base URL:', authBaseURL);

const api = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30_000,
});

const authApi = axios.create({
  baseURL: authBaseURL,
  withCredentials: true,
  timeout: 30_000,
});

const clearSessionAndRedirect = () => {
  clearLocalSession();
  redirectToUnifiedAuth();
};

const attachBearer = (config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

api.interceptors.request.use(attachBearer, (error) => Promise.reject(error));
authApi.interceptors.request.use(attachBearer, (error) => Promise.reject(error));

const shouldFallbackLegacy = (error) => {
  const status = error?.response?.status;
  return status === 404 || status === 405;
};

export const postAuthLogin = async (payload) => {
  try {
    return await authApi.post('auth/login/', payload);
  } catch (error) {
    if (!shouldFallbackLegacy(error)) {
      throw error;
    }
    return api.post('/repairs/token/', payload);
  }
};

export const postAuthRefresh = async (refreshToken) => {
  try {
    return await authApi.post('auth/refresh/', { refresh: refreshToken });
  } catch (error) {
    if (!shouldFallbackLegacy(error)) {
      throw error;
    }
    return axios.post(`${baseURL}repairs/token/refresh/`, { refresh: refreshToken }, { withCredentials: true });
  }
};

export const postAuthLogout = async (refreshToken) => {
  try {
    return await authApi.post('auth/logout/', { refresh: refreshToken });
  } catch (error) {
    if (!shouldFallbackLegacy(error)) {
      throw error;
    }
    return Promise.resolve({ data: {} });
  }
};

export const getAuthMe = async () => {
  try {
    return await authApi.get('auth/me/');
  } catch (error) {
    if (!shouldFallbackLegacy(error)) {
      throw error;
    }
    return api.get('/core/users/me/');
  }
};

export const getTelegramBotLink = async () => {
  try {
    return await authApi.get('telegram/bot-link/');
  } catch (error) {
    if (!shouldFallbackLegacy(error)) {
      throw error;
    }
    return api.get('telegram/bot_link/');
  }
};

export const postTelegramMiniAppAuth = async (payload) => {
  try {
    return await authApi.post('telegram/miniapp-auth/', payload);
  } catch (error) {
    if (!shouldFallbackLegacy(error)) {
      throw error;
    }
    return api.post('/repairs/telegram/miniapp-auth/', payload);
  }
};

export const postTelegramLoginLink = async (payload) => {
  try {
    return await authApi.post('telegram/login-link/', payload);
  } catch (error) {
    if (!shouldFallbackLegacy(error)) {
      throw error;
    }
    return api.post('/repairs/telegram/login-link/', payload);
  }
};

export const getTelegramLoginStatus = async (params) => {
  try {
    return await authApi.get('telegram/login-status/', { params });
  } catch (error) {
    if (!shouldFallbackLegacy(error)) {
      throw error;
    }
    return api.get('/repairs/telegram/login-status/', { params });
  }
};

let isRedirecting = false;
let refreshPromise = null;

const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) throw new Error('Refresh token is missing');
  refreshPromise = postAuthRefresh(refreshToken)
    .then((response) => {
      const newAccessToken = response.data?.access;
      if (!newAccessToken) throw new Error('Refresh succeeded without access token');
      localStorage.setItem('access_token', newAccessToken);
      if (response.data?.refresh) localStorage.setItem('refresh_token', response.data.refresh);
      window.dispatchEvent(new CustomEvent('auth-token-refreshed'));
      return newAccessToken;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
};

const shouldAttemptRefresh = (error) => {
  const status = error?.response?.status;
  if (status === 401) {
    return true;
  }
  if (status !== 403) {
    return false;
  }

  const data = error?.response?.data || {};
  const code = String(data?.code || '').toLowerCase();
  const detail = typeof data?.detail === 'string' ? data.detail.toLowerCase() : '';
  const messages = Array.isArray(data?.messages) ? data.messages : [];
  const hasTokenMessage = messages.some((item) => {
    const message = String(item?.message || '').toLowerCase();
    return message.includes('token');
  });

  return code === 'token_not_valid' || detail.includes('token') || hasTokenMessage;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.data?.code === 'basket_stage_conflict') {
      window.dispatchEvent(new CustomEvent('basket-stage-conflict', { detail: error.response.data }));
    }
    const originalRequest = error.config || {};
    const url = String(originalRequest?.url || '');
    const isAuthRequest = url.includes('auth/login/') || url.includes('auth/refresh/') || url.includes('repairs/token/');

    if (
      shouldAttemptRefresh(error)
      && !isRedirecting
      && !originalRequest._retry
      && !isAuthRequest
    ) {
      originalRequest._retry = true;
      if (localStorage.getItem('refresh_token')) {
        try {
          const newAccessToken = await refreshAccessToken();

          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          console.error('Axios: token refresh failed:', refreshError);
          isRedirecting = true;
          clearSessionAndRedirect();
          setTimeout(() => {
            isRedirecting = false;
          }, 1000);
        }
      } else {
        isRedirecting = true;
        clearSessionAndRedirect();
        setTimeout(() => {
          isRedirecting = false;
        }, 1000);
      }
    }

    const method = String(originalRequest.method || 'get').toLowerCase();
    const status = error?.response?.status;
    const isTransientFailure = !status
      || status === 502
      || status === 503
      || status === 504
      || error.code === 'ECONNABORTED'
      || error.code === 'ERR_NETWORK';
    if (method === 'get' && isTransientFailure && !originalRequest._networkRetry && !originalRequest._skipNetworkRetry) {
      originalRequest._networkRetry = true;
      await new Promise(resolve => setTimeout(resolve, 700));
      return api(originalRequest);
    }

    return Promise.reject(error);
  },
);

export { api as default, authApi, baseURL, authBaseURL };
